import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartLineUp,
  Warning,
  CheckCircle,
  ArrowRight,
  TrendUp,
  Brain,
  Target,
  Question,
  CaretDown,
  Notebook,
  Clock,
  ListChecks,
  Gear,
  Lightning,
  ChartBar,
  Table,
  X,
  Check
} from 'phosphor-react';
import { updateSeoMetadata, getArticleAlternates } from '../../lib/seo';

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

const DiarioManualVsAutomatico = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'Diário de Trades Manual vs Automático | Qual Escolher?',
      description: 'Compare diário de trades manual vs automático. Descubra qual método é mais eficiente para melhorar seus resultados no trading.',
      path: '/pt/diario-de-trades-manual-vs-automatico',
      lang: 'pt-BR',
      alternates: getArticleAlternates('/pt/diario-de-trades-manual-vs-automatico'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  const [openFaq, setOpenFaq] = React.useState(null);

  const faqs = [
    {
      question: "Planilha de Excel serve como diário de trades?",
      answer: "Tecnicamente sim, mas na prática a maioria dos traders abandona após algumas semanas. O problema não é a planilha em si, mas o atrito do preenchimento manual. Cada campo que você precisa preencher é uma barreira. Diários automáticos eliminam esse atrito capturando dados diretamente das suas operações."
    },
    {
      question: "Diário automático substitui completamente a análise manual?",
      answer: "Não completamente. O diário automático cuida da coleta e organização de dados, mas a reflexão sobre suas decisões ainda é sua responsabilidade. A diferença é que você gasta seu tempo analisando padrões em vez de digitando números. O automático potencializa sua análise, não a substitui."
    },
    {
      question: "Vale a pena pagar por um diário automático?",
      answer: "Depende do quanto você leva trading a sério. Se você opera casualmente, uma planilha pode ser suficiente. Mas se trading é parte significativa da sua renda ou você quer evoluir profissionalmente, o tempo economizado e a qualidade das análises de um sistema automático se pagam rapidamente."
    },
    {
      question: "Quanto tempo eu economizo com um diário automático?",
      answer: "A maioria dos traders gasta 15-30 minutos por dia preenchendo um diário manual. Com um sistema automático, esse tempo cai para 2-5 minutos de revisão. Ao longo de um mês, são várias horas economizadas que você pode usar para estudar, analisar ou simplesmente descansar."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <nav className="border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-10 md:h-11 w-auto object-contain"
            />
          </Link>
          <Link 
            to="/signup"
            className="px-4 py-2 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors text-sm"
          >
            Começar Grátis
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 pt-16 pb-20">
        
        <header className="mb-12">
          <div className="flex items-center gap-2 text-[#00E676] text-sm font-medium mb-4">
            <Notebook size={16} weight="fill" />
            <span>Controle de Trades</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
            Diário de Trades Manual vs Automático
          </h1>
          <p className="text-lg text-white/60">
            Comparação completa para você escolher o melhor método
          </p>
        </header>

        <div className="bg-gradient-to-r from-[#00E676]/10 to-transparent border-l-2 border-[#00E676] p-6 rounded-r-lg mb-12">
          <p className="text-white/80 leading-relaxed">
            A diferença entre um trader que evolui e um que estagna frequentemente está no método de registro. 
            Diário manual exige disciplina constante. Diário automático elimina o atrito. 
            Este guia compara os dois métodos para você tomar uma decisão informada.
          </p>
        </div>

        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
              <Table size={20} className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold">O Diário Manual</h2>
          </div>
          
          <p className="text-white/70 leading-relaxed mb-6">
            Planilhas de Excel, Google Sheets, cadernos físicos. O diário manual é o método tradicional 
            que a maioria dos traders começa usando. Você abre o trade, fecha, e depois registra 
            manualmente todos os detalhes.
          </p>

          <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
              <Check size={18} className="text-green-400" />
              Vantagens do Método Manual
            </h3>
            <ul className="space-y-3 text-white/70">
              <li className="flex items-start gap-3">
                <CheckCircle size={18} className="text-green-400 mt-1 flex-shrink-0" />
                <span><strong className="text-white">Custo zero</strong> — planilhas são gratuitas e acessíveis</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle size={18} className="text-green-400 mt-1 flex-shrink-0" />
                <span><strong className="text-white">Customização total</strong> — você cria exatamente o que precisa</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle size={18} className="text-green-400 mt-1 flex-shrink-0" />
                <span><strong className="text-white">Processo de reflexão</strong> — escrever força você a pensar sobre o trade</span>
              </li>
            </ul>
          </div>

          <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-6">
            <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
              <X size={18} className="text-red-400" />
              Problemas do Método Manual
            </h3>
            <ul className="space-y-3 text-white/70">
              <li className="flex items-start gap-3">
                <Warning size={18} className="text-red-400 mt-1 flex-shrink-0" />
                <span><strong className="text-white">Alto atrito</strong> — cada trade requer 5-10 minutos de preenchimento</span>
              </li>
              <li className="flex items-start gap-3">
                <Warning size={18} className="text-red-400 mt-1 flex-shrink-0" />
                <span><strong className="text-white">Erros de digitação</strong> — números errados corrompem toda a análise</span>
              </li>
              <li className="flex items-start gap-3">
                <Warning size={18} className="text-red-400 mt-1 flex-shrink-0" />
                <span><strong className="text-white">Taxa de abandono alta</strong> — 80%+ dos traders param após 2-4 semanas</span>
              </li>
              <li className="flex items-start gap-3">
                <Warning size={18} className="text-red-400 mt-1 flex-shrink-0" />
                <span><strong className="text-white">Análises limitadas</strong> — fórmulas complexas são difíceis de implementar</span>
              </li>
            </ul>
          </div>
        </section>

        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
              <Lightning size={20} className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold">O Diário Automático</h2>
          </div>
          
          <p className="text-white/70 leading-relaxed mb-6">
            Sistemas que capturam seus trades automaticamente, seja por integração com corretoras, 
            importação de dados, ou extração de informações de screenshots. O registro acontece 
            sem você precisar digitar cada número.
          </p>

          <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
              <Check size={18} className="text-green-400" />
              Vantagens do Método Automático
            </h3>
            <ul className="space-y-3 text-white/70">
              <li className="flex items-start gap-3">
                <CheckCircle size={18} className="text-green-400 mt-1 flex-shrink-0" />
                <span><strong className="text-white">Zero atrito</strong> — dados capturados automaticamente</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle size={18} className="text-green-400 mt-1 flex-shrink-0" />
                <span><strong className="text-white">Precisão garantida</strong> — sem erros de digitação</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle size={18} className="text-green-400 mt-1 flex-shrink-0" />
                <span><strong className="text-white">Análises avançadas</strong> — métricas calculadas instantaneamente</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle size={18} className="text-green-400 mt-1 flex-shrink-0" />
                <span><strong className="text-white">Consistência natural</strong> — você mantém o hábito porque é fácil</span>
              </li>
            </ul>
          </div>

          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-6">
            <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
              <Warning size={18} className="text-amber-400" />
              Considerações do Método Automático
            </h3>
            <ul className="space-y-3 text-white/70">
              <li className="flex items-start gap-3">
                <Warning size={18} className="text-amber-400 mt-1 flex-shrink-0" />
                <span><strong className="text-white">Custo mensal</strong> — ferramentas profissionais geralmente são pagas</span>
              </li>
              <li className="flex items-start gap-3">
                <Warning size={18} className="text-amber-400 mt-1 flex-shrink-0" />
                <span><strong className="text-white">Curva de aprendizado</strong> — você precisa aprender a usar a ferramenta</span>
              </li>
            </ul>
          </div>
        </section>

        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
              <ChartBar size={20} className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold">Comparação Direta</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-4 text-white/60 font-medium">Critério</th>
                  <th className="text-center py-4 px-4 text-white/60 font-medium">Manual</th>
                  <th className="text-center py-4 px-4 text-[#00E676] font-medium">Automático</th>
                </tr>
              </thead>
              <tbody className="text-white/80">
                <tr className="border-b border-white/5">
                  <td className="py-4 px-4">Tempo por trade</td>
                  <td className="py-4 px-4 text-center">5-10 min</td>
                  <td className="py-4 px-4 text-center text-[#00E676]">30 seg</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-4 px-4">Taxa de abandono</td>
                  <td className="py-4 px-4 text-center">80%+</td>
                  <td className="py-4 px-4 text-center text-[#00E676]">&lt;20%</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-4 px-4">Precisão dos dados</td>
                  <td className="py-4 px-4 text-center">Variável</td>
                  <td className="py-4 px-4 text-center text-[#00E676]">99%+</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-4 px-4">Análises avançadas</td>
                  <td className="py-4 px-4 text-center">Difícil</td>
                  <td className="py-4 px-4 text-center text-[#00E676]">Automático</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-4 px-4">Custo</td>
                  <td className="py-4 px-4 text-center text-[#00E676]">Grátis</td>
                  <td className="py-4 px-4 text-center">Pago</td>
                </tr>
                <tr>
                  <td className="py-4 px-4">Customização</td>
                  <td className="py-4 px-4 text-center text-[#00E676]">Total</td>
                  <td className="py-4 px-4 text-center">Estruturada</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
              <Target size={20} className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold">Quando Usar Cada Um</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="font-semibold text-white mb-4">Diário Manual é Para Você Se:</h3>
              <ul className="space-y-3 text-white/70">
                <li className="flex items-start gap-2">
                  <span className="text-[#00E676]">•</span>
                  <span>Está começando e quer custo zero</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#00E676]">•</span>
                  <span>Opera poucos trades por semana</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#00E676]">•</span>
                  <span>Tem disciplina comprovada para manter hábitos</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#00E676]">•</span>
                  <span>Precisa de campos muito específicos</span>
                </li>
              </ul>
            </div>

            <div className="bg-[#00E676]/5 border border-[#00E676]/20 rounded-xl p-6">
              <h3 className="font-semibold text-white mb-4">Diário Automático é Para Você Se:</h3>
              <ul className="space-y-3 text-white/70">
                <li className="flex items-start gap-2">
                  <span className="text-[#00E676]">•</span>
                  <span>Opera com frequência (day trade, scalping)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#00E676]">•</span>
                  <span>Já abandonou diários manuais antes</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#00E676]">•</span>
                  <span>Quer análises profundas sem esforço</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#00E676]">•</span>
                  <span>Leva trading a sério como fonte de renda</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
              <Brain size={20} className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold">A Verdade Sobre Consistência</h2>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
            <p className="text-white/70 leading-relaxed mb-4">
              O melhor diário é aquele que você realmente usa. Não adianta ter a planilha mais 
              completa do mundo se você abandona após duas semanas.
            </p>
            <p className="text-white/70 leading-relaxed mb-4">
              A maioria dos traders que falham com diários manuais não falharam por falta de 
              disciplina — falharam porque o sistema exigia esforço demais para pouco retorno 
              percebido no curto prazo.
            </p>
            <p className="text-white/80 leading-relaxed">
              <strong className="text-white">Diários automáticos resolvem esse problema de design.</strong> 
              Eles reduzem o atrito ao mínimo, tornando a consistência quase inevitável.
            </p>
          </div>
        </section>

        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
              <Question size={20} className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold">Perguntas Frequentes</h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-white/10 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium text-white pr-4">{faq.question}</span>
                  <CaretDown 
                    size={20} 
                    className={`text-white/60 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 text-white/70 leading-relaxed border-t border-white/5 pt-4">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <div className="bg-gradient-to-br from-[#00E676]/10 via-transparent to-transparent border border-[#00E676]/20 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4">Experimente o Diário Automático</h2>
            <p className="text-white/70 mb-6 leading-relaxed">
              GENI captura seus trades automaticamente a partir de screenshots. 
              Sem digitação, sem erros, sem desculpas para não registrar.
            </p>
            <Link 
              to="/signup"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#00E676] text-black font-semibold rounded-lg hover:bg-[#00E676]/90 transition-colors"
            >
              Começar Grátis
              <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-4">Artigos Relacionados</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link 
              to="/pt/diario-de-trades-automatico"
              className="p-4 bg-white/5 border border-white/10 rounded-xl hover:border-[#00E676]/50 transition-colors group"
            >
              <h3 className="font-medium text-white mb-1 group-hover:text-[#00E676] transition-colors">
                Diário de Trades Automático
              </h3>
              <p className="text-sm text-white/50">Como funciona e por que usar</p>
            </Link>
            <Link 
              to="/pt/por-que-traders-nao-evoluem"
              className="p-4 bg-white/5 border border-white/10 rounded-xl hover:border-[#00E676]/50 transition-colors group"
            >
              <h3 className="font-medium text-white mb-1 group-hover:text-[#00E676] transition-colors">
                Por Que Traders Não Evoluem
              </h3>
              <p className="text-sm text-white/50">Os erros mais comuns no processo de melhoria</p>
            </Link>
          </div>
        </section>

      </article>

      <footer className="border-t border-white/10 py-12">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center">
              <img 
                src={LOGO_HORIZONTAL}
                alt="GENI" 
                className="h-8 w-auto object-contain"
              />
            </Link>
            <div className="flex items-center gap-6 text-sm text-white/40">
              <Link to="/about" className="hover:text-white transition-colors">Sobre</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Termos</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacidade</Link>
            </div>
            <p className="text-sm text-white/30">© 2025 GENI</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DiarioManualVsAutomatico;
